<template>
  <div>
    <data-table
      ref="dataTableRef"
      :config="dataTableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';

interface DataTableInstance {
  loadData: () => void;
}

// Get i18n composable
const { t } = useI18n();
const { initialized } = useFiltro();

// Reactive data
const dataTableRef = ref<DataTableInstance | null>(null);
const dynamicColumns = ref<string[]>([]);
const dataLoaded = ref<boolean>(false);

// Computed properties
const endpoint = computed(() => '/index.php/relatorios/recebimento_operadores/buscaDados');

const tableColumns = computed(() => {
  // Base columns that are always present
  const baseColumns = [
    {
      data: 'Nome',
      title: t('Nome'),
      width: 200,
      sortable: true
    },
    {
      data: 'Franquia',
      title: t('Franquia'),
      width: 200,
      sortable: true,
      footerType: 'unique'
    }
  ];

  // Add dynamic payment method columns
  const paymentColumns = dynamicColumns.value.map(colName => ({
    data: colName,
    title: colName,
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum'
  }));

  // Total column (always last)
  const totalColumn = {
    data: 'Total',
    title: t('Total'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum'
  };

  return [...baseColumns, ...paymentColumns, totalColumn];
});

const dataTableConfig = computed(() => {
  return {
    tableId: 'tabelaRecebimentoOperadores',
    filename: `recebimento_operadores_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 100,
    pagination: true,
    footerLabel: t('Totais'),
    rowSelection: 'single',
    resizableColumns: true,
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const filterConfig = computed(() => {
  const baseFilters = {
    'Nome': 'text',
    'Franquia': 'select',
    'Total': 'range'
  };

  // Add range filters for dynamic payment columns
  const paymentFilters: Record<string, string> = {};
  dynamicColumns.value.forEach(colName => {
    paymentFilters[colName] = 'range';
  });

  return {
    searchName: t('Procurar'),
    choosePlaceholder: t('Escolha'),
    refreshName: t('Atualizar'),
    printName: t('Imprimir'),
    debug: false,
    columns: {
      ...baseFilters,
      ...paymentFilters
    }
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV'),
    loading: false
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

// Methods
const loadData = () => {
  const stack = new Error().stack;
  log('RecebimentoOperadoresTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n') // Log first 3 stack frames
  });
  if (dataTableRef.value) {
    dataTableRef.value.loadData();
  }
};

const extractDynamicColumns = (data: unknown[]): string[] => {
  if (data && data.length > 0) {
    const firstRow = data[0] as Record<string, unknown>;
    const columns = Object.keys(firstRow).filter(
      key => key !== 'Nome' && key !== 'Franquia' && key !== 'Total' && !key.endsWith('_display')
    );
    log('RecebimentoOperadoresTable', 'extractDynamicColumns', 'Extracted dynamic columns', columns);
    return columns;
  }
  return [];
};

const handleDataLoaded = (data: unknown[]): void => {
  log('RecebimentoOperadoresTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');

  const newDynamicColumns = extractDynamicColumns(data);

  if (JSON.stringify(newDynamicColumns) !== JSON.stringify(dynamicColumns.value)) {
    log('RecebimentoOperadoresTable', 'handleDataLoaded', 'Updating dynamic columns');
    dynamicColumns.value = newDynamicColumns;
  }

  dataLoaded.value = true;
  eventBus.emit('globalMessage.success', t(`Carregados`) + ` ${data.length} ` + t(`registros de recebimentos`));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('RecebimentoOperadoresTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : error.message || '';
  eventBus.emit('globalMessage.error', t('Erro ao carregar recebimentos dos operadores: ') + message);
};

// Named handler for proper cleanup
const onFilterUpdated = (): void => {
  log('RecebimentoOperadoresTable', 'event:filter.updated', 'Received, reloading data');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('RecebimentoOperadoresTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  // Only reload when filter is explicitly updated
  log('RecebimentoOperadoresTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  eventBus.on('filter.updated', onFilterUpdated);
});

onBeforeUnmount(() => {
  log('RecebimentoOperadoresTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('filter.updated', onFilterUpdated);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
</style>
